import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ExpandMore } from '@material-ui/icons';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { TreeView, TreeItem } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Box, Typography, Paper, Tooltip, TextField, InputAdornment } from '@material-ui/core';
import { handleSnackBar, setFocusElement, setIsDocumentClicked, setReRender, setTabOnFocus } from '../../../store/global/actions';
import { useDispatch, useSelector } from 'react-redux';
import { getTransactionElements } from '../../../utils/commonUtils';
import { getCodeEditStatus, getElementOnFocus, getReRender } from '../../../store/global/selectors';
import AddIcon from '@material-ui/icons/Add';
import AddClusteretsAlert from '../alerts/transaction/AddClusteretsAlert';
import RemoveClusterletAlert from '../alerts/transaction/RemoveClusterletAlert';
import DeleteEventAlert from '../alerts/DeleteEventAlert';
import DeleteTransactionAlert from '../alerts/DeleteTransactionAlert';
import SearchIcon from '@material-ui/icons/Search';
import useDebounce from '../../../hooks/useDebounce';
import { DeleteIcon } from '../icons';

const useStyles = makeStyles((theme) => ({
    treeItemLabel: {
        fontWeight: 600,
        color: theme.palette.text.primary,
        backgroundColor: '#ffffff',
        padding: theme.spacing(0.5, 1),
    },
    treeItemContentExpanded: {
        backgroundColor: '#ffffff',
    },
    deleteButton: {
        color: theme.palette.error.light,
        padding: 2,
        '&:hover': {
            color: theme.palette.error.main,
        },
        '&:focus': {
            outline: 'none',
        },
    },
    addClusterlet: {
        '&:focus': {
            outline: 'none',
        },
    },
    smallText: {
        fontSize: '0.8rem',
    },
    eventDescription: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.3),
    },
    childHandler: {
        fontSize: '0.75rem',
        paddingLeft: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
}));

const StyledTreeItem = (props) => {
    const classes = useStyles();
    return (
        <TreeItem
            {...props}
            classes={{
                label: classes.treeItemLabel,
                content: classes.treeItemContentExpanded,
            }}
        />
    );
};

const TransactionElements = ({data, onDeleteConfirmation, expandParentOnItemSelection}) => {
    // console.log("TransactionElements-------------------------------", {data})
    const classes = useStyles();
    const dispatch = useDispatch();

    const elementOnFocus = useSelector(getElementOnFocus);
    
    const reRender = useSelector(getReRender);
    
    const [transaction, setTransaction] = useState(data?.transaction) ;
    const [transactionElements, setTransactionElements] = useState(getTransactionElements(data));
    const [actualTransactionElements, setActualTransactionElements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddNodes, setShowAddNodes] = useState(false);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [removeClusterlet, setRemoveClusterlet] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState((elementOnFocus?.type === "invoked_transaction" ? elementOnFocus?.uuid : elementOnFocus?.id) || elementOnFocus?.transaction);
    const [showEventDeleteAlert, setShowEventDeleteAlert] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showDeleteTransactionAlert, setShowDeleteTransactionAlert] = useState(false);
    const [transactionClusterlets, setTransactionClusterlets] = useState(data?.clusterlets);
    const [searchValue, setSearchValue] = useState(null)
    const debouncedSearchValue = useDebounce(searchValue, 300)
    const codeEditStatus = useSelector(getCodeEditStatus);


    useEffect(() => {
        if(data){
            const result = getTransactionElements(data);
            setTransactionClusterlets(result?.clusterlets);
            setTransaction(data?.transaction);
            setActualTransactionElements(result);
        }
    },[data])

    useMemo(() => {
        if (!debouncedSearchValue || debouncedSearchValue === "") {
            setTransactionElements(actualTransactionElements);
            return;
          }
        
          const search = debouncedSearchValue.toLowerCase();

          const filteredData = searchTree(search);
          setTransactionElements(filteredData)
    },[debouncedSearchValue, actualTransactionElements])

    useEffect(() => {
        setSelectedNodeId( (elementOnFocus?.type === "invoked_transaction" ? elementOnFocus?.uuid : elementOnFocus?.id) || elementOnFocus?.transaction);
    }, [elementOnFocus])


    const nodeIds = new Set([
        'transactions',
        'clusterlets',
        'events',
        'child_transactions',
        'invoked_transactions',
    ]);

    function searchTree(search){
        const filtered = {
            transaction: actualTransactionElements.transaction?.name?.toLowerCase().includes(search)
              ? actualTransactionElements.transaction
              : null,
        
            clusterlets: actualTransactionElements.clusterlets?.filter(cl =>
              cl.name?.toLowerCase().includes(search)
            ) || [],
        
            events: actualTransactionElements.events?.filter(ev =>
              ev.name?.toLowerCase().includes(search)
            ) || [],
        
            child_transactions: actualTransactionElements.child_transactions?.filter(tx =>
              tx.name?.toLowerCase().includes(search)
            ) || [],
        
            invoked_transactions: actualTransactionElements.invoked_transactions?.filter(tx =>
              tx.name?.toLowerCase().includes(search)
            ) || [],
          };
        
          return filtered;
    }

    const findClusterletType = (type) => {
        return type === 1 ? 'Delegate' : 'Clusterlet';
    };

    const handleItemSelection = ({item, type}) => {
        try {
            console.log("handleItemSelection-------------------", {item, type})

            if (codeEditStatus) {
                dispatch(setIsDocumentClicked());
                console.log("Code edit status is true")
                return;
            }

            if(item === undefined || item?._id === selectedNodeId){return}

            setSelectedNodeId(item?._id);

            let focusItem = {
                transaction: elementOnFocus?.transaction,
                id: item?._id,
                name: item?.name,
                desc: item?.desc || "",
                type,
                item,
                action: "edit"
            }
            if(type === "clusterlet"){
                const itemType = item?.type
                const clusterletType = itemType === 1 ? "external_function" : itemType === 2 ? "clusterlet" : "analytics"
                focusItem.type = clusterletType;
                focusItem.icon = item?.icon;

                if(item?.type === 1) focusItem.protocol = item?.attributes?.type;
            }else if(type === "event"){
                const delegateData = item.source?.type === 1 ? item.source : item.target;
                focusItem.protocol = delegateData?.attributes?.type;
            }else if(type === "nested_transaction" || type === "invoked_transaction"){
                focusItem.uuid  = item?.uuid;
                focusItem.parent  = item?.parent;
            }

            // Expand parent in the diagram
            if(expandParentOnItemSelection && type !== "clusterlet"){
                expandParentOnItemSelection({id: focusItem?.uuid ? focusItem?.uuid : focusItem?.id, type})
            }
            
            // console.log({focusItem})
            // to update the redux state
            dispatch(setFocusElement(focusItem));
            dispatch(setTabOnFocus(0));



        } catch (error) {
            console.error(`Handling item selection in the sidebar elements failed. ${error}`)
            return;
        }
    };

    const handleRemoveClusterlet = (id) => {
        if (codeEditStatus) {
            dispatch(setIsDocumentClicked());
            console.log("Code edit status is true")
            return;
        }
        setRemoveClusterlet(id);
    };


    const onAddClusterletsClose = () => {
        setShowAddNodes(false);
    }

    const onAddclusterletsCompletion = () => {

    }

    const truncateLabel = (text, maxLength = 25) =>  {
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + '...';
    }

    const handleDeleteItem = (type, item) => {
        try {
            // console.log("handleDeleteItem-------------------", {type, item})
            if (codeEditStatus) {
                dispatch(setIsDocumentClicked());
                console.log("Code edit status is true")
                return;
            }
            
            const deleteItem ={
                id: item?._id,
                name: item?.name,
                type: item?.type,
                item,
            }

            if(type === "event"){
                setShowEventDeleteAlert(true);
            }else if(type === "child_transaction"){
                deleteItem.parent = item?.parent;
                setShowDeleteTransactionAlert(true);
            }else if(type === "invoked_transaction"){
                deleteItem.uuid = item?.uuid;
                deleteItem.parent = item?.parent;
                setShowDeleteTransactionAlert(true);
            }
            // console.log({deleteItem})
            setItemToDelete(deleteItem);


        } catch (error) {
            console.error(`Handling item deletion failed. ${error}`);
        }
        
    };




    const renderClusterlets = () =>{

        return transactionElements?.clusterlets?.map((item) => (
            <StyledTreeItem
                className={`clusterlet-item-${item?.name}`}
                nodeId={item._id}
                key={item._id}
                label={
                    <Box width="100%" onClick={(e) => {
                        e.stopPropagation();
                        handleItemSelection({item, type: 'clusterlet'} );
                    }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography className={classes.smallText}>{item.name}</Typography>
                            <Tooltip title="Remove clusterlet">
                                <IconButton
                                    data-ui-tour-id="remove-clusterlet-button"
                                    className={classes.deleteButton}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveClusterlet(item._id);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography className={classes.eventDescription}>
                            {findClusterletType(item?.type)}
                        </Typography>
                    </Box>
                }
            />
        ));
    }


    const renderEvents = (events, showTypeWithLabel=false) => {
        return events?.map((event) => (
            <StyledTreeItem
                id={`event_item_${event.name}`}
                nodeId={event._id}
                key={event._id}
                label={
                    <Box width="100%" onClick={(e) => {
                        e.stopPropagation();
                        handleItemSelection({item: event, type: 'event'});
                    }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography className={classes.smallText}>{showTypeWithLabel ? `${truncateLabel(event.name)} (Event)` : truncateLabel(event.name)} </Typography>
                            <Tooltip title="Delete event">
                                <IconButton
                                    className={classes.deleteButton}
                                    size="small"
                                    id={`delete_${event.name}_btn`}
                                    data-ui-tour-id="delete-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteItem("event", event);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography className={classes.eventDescription}>{event?.source?.name}</Typography>
                            <ArrowRightAltIcon style={{ color: 'grey' }} />
                            <Typography className={classes.eventDescription}>{event?.target?.name}</Typography>
                        </Box>
                    </Box>
                }
            >
                {/* {event?.attributes?.onsend && (
                    <StyledTreeItem
                        nodeId={`${event._id}_onsend`}
                        label={
                            <Typography
                                className={classes.childHandler}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // handleItemSelection(event._id, 'event', true);
                                }}
                            >
                                Onsend handler
                            </Typography>
                        }
                    />
                )}
                {event?.attributes?.onrecv && (
                    <StyledTreeItem
                        nodeId={`${event._id}_onrecv`}
                        label={
                            <Typography
                                className={classes.childHandler}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // handleItemSelection(event._id, 'event', true);
                                }}
                            >
                                Onrecv handler
                            </Typography>
                        }
                    />
                )} */}
            </StyledTreeItem>
        ));
    }

    const renderChildTxns = (transactions, showTypeWithLabel=false) => {
        return transactions?.map((child) => (
            <StyledTreeItem
                nodeId={`${child?._id}`}
                label={
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleItemSelection({item: child, type: "nested_transaction"});
                        }}
                    >
                        <Typography className={classes.smallText} style={{ fontWeight: '600', color: 'grey' }}>
                            {showTypeWithLabel ?  `${truncateLabel(child?.name)} (Child transaction)` : truncateLabel(child?.name)}
                        </Typography>

                        <Tooltip title="Delete child">
                            <IconButton
                                data-ui-tour-id="delete-button"
                                className={classes.deleteButton}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteItem("child_transaction", child);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                        </IconButton>
                        </Tooltip>
                    </Box>
                }
            >
                {child?.events?.length > 0 && renderEvents(child?.events, true)}
                {child?.child_transactions?.length > 0 && renderChildTxns(child?.child_transactions, true)}
                {child?.invoked_transactions?.length > 0 && renderInvoked(child?.invoked_transactions, true)}
            </StyledTreeItem>
        ));

    }

    const renderInvoked = (transactions, showTypeWithLabel=false) => {
        return transactions?.map((txn) => (
            <StyledTreeItem
                nodeId={txn.uuid}
                key={txn.uuid}
                label={
                    <Box
                        width="100%"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleItemSelection({item: txn, type: "invoked_transaction"});
                        }}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography className={classes.smallText}>{showTypeWithLabel ? `${txn.name} (Invoked transaction)` : txn.name}</Typography>
                            <Tooltip title="Delete invoked">
                                <IconButton
                                    data-ui-tour-id="delete-button"
                                    className={classes.deleteButton}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteItem("invoked_transaction", txn);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                        </Box>
                    </Box>
                }
            />
        ));
    }


    const handleAddClusterlet = () => {
        if (codeEditStatus) {
            dispatch(setIsDocumentClicked());
            console.log("Code edit status is true")
            return;
        }
        setShowAddNodes(true);
    };

    const handleRemoveClusterletClose = () => {
        setRemoveClusterlet(false);
    }

    const onEventDeleteComplete = () => {
        setShowEventDeleteAlert(false);
        dispatch(handleSnackBar({ open: true, message: 'Event deleted', severity: 'success' }));
        dispatch(setReRender());
        
        if(elementOnFocus?.id === itemToDelete?.id){
            setSelectedNodeId(transaction?._id);
            dispatch(setFocusElement({
                transaction: transaction?._id,
                id: transaction?._id,
                name: transaction?.name,
                desc: transaction?.desc || "",
                type: "transaction",
                action: "edit",
                item: transaction
            }));
            dispatch(setTabOnFocus(0));
        } 
    }

    const onDeleteTransactionCompletion = (type, action) => {
        if(action === "delete"){
            dispatch(handleSnackBar({ open: true, message: `${type} deleted`, severity: "success" }));
            setShowDeleteTransactionAlert(false);
            setItemToDelete(null);
            dispatch(setReRender());

            if(elementOnFocus?.id === itemToDelete?.id){
                setSelectedNodeId(transaction?._id);
                dispatch(setFocusElement({
                    transaction: transaction?._id,
                    id: transaction?._id,
                    name: transaction?.name,
                    desc: transaction?.desc || "",
                    type: "transaction",
                    action: "edit",
                    item: transaction
                }));
                dispatch(setTabOnFocus(0));
            } 


            
        }
    }

    const onCloseEventDelete = () => {
        setShowEventDeleteAlert(false);
        setItemToDelete(null);
    }

    const OnDeleteTransactionClose = () => {
        setShowDeleteTransactionAlert(false);
        setItemToDelete(null);
    }


    return (
        <Paper id={"transaction_elements_container"} elevation={2} style={{ width: '100%', height: '100%', border: '0', boxShadow: 'none', paddingLeft: '16px', paddingTop: 0, paddingBottom: '10' }}>
            {loading &&
            <Box style={{ height: '100%', width: '100%' }} display="flex" justifyContent="center" alignItems="center" gap={2}>
                Loading...
            </Box> }
            
            {!loading && data && 
            <>
                <TextField 
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  style={{ width: '100%' }}
                  onChange={(e) => setSearchValue(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18, color: '#666' }} />
                      </InputAdornment>
                    ),
                    style: {
                      paddingTop: 2,
                      paddingBottom: 2,
                      height: 36,
                      margin: '8px 8px 8px 0px'
                    }
                  }}
                  inputProps={{
                    style: {
                      padding: '6px 8px',
                      fontSize: '13px'
                    }
                  }}
                />
                <TreeView
                    id={"transaction_elements_tree"}
                    aria-label="transaction-tree"
                    defaultCollapseIcon={<ExpandMore />}
                    defaultExpandIcon={<ChevronRight />}
                    defaultExpanded={[...nodeIds]}
                    selected={selectedNodeId}
                >
                    <StyledTreeItem nodeId="transactions" label="Transaction">
                        <StyledTreeItem
                            nodeId={transaction?._id}
                            label={
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleItemSelection({item: transaction, type: "transaction"});
                                    }}
                                >
                                    <Typography className={classes.smallText}>{transaction?.name}</Typography>
                                </Box>
                            }
                        />
                    </StyledTreeItem>

                    {/* Clusterlets */}
                    <StyledTreeItem
                        nodeId="clusterlets"
                        label={
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                onClick={(e) => e.stopPropagation()} // Prevent collapse/expand on label click
                            >
                                <Typography style={{fontWeight: 600}}>
                                    Clusterlets ({transactionElements?.clusterlets?.length})
                                </Typography>
                                <Tooltip title="Add clusterlets">
                                    <IconButton
                                        data-ui-tour-id="add-clusterlet-button"
                                        classNamme={classes?.addClusterlet}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent TreeView selection
                                            handleAddClusterlet(); // Your custom function
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    >
                        {renderClusterlets(transactionElements?.clusterlets)}
                    </StyledTreeItem>

                    <StyledTreeItem nodeId="events" label={`Events (${transactionElements?.events?.length})`}>
                        {renderEvents(transactionElements?.events || [])}
                    </StyledTreeItem>


                    {transactionElements?.child_transactions?.length > 0 && (
                        <StyledTreeItem nodeId="child_transactions" label={`Child Transactions (${transactionElements?.child_transactions.length})`}>
                            {renderChildTxns(transactionElements?.child_transactions || [])}
                        </StyledTreeItem>
                    )}

                    {transactionElements?.invoked_transactions?.length > 0 && (
                        <StyledTreeItem nodeId="invoked_transactions" label={`Invoked Transactions (${transactionElements?.invoked_transactions.length})`}>
                            {renderInvoked(transactionElements?.invoked_transactions || [])}
                        </StyledTreeItem>
                    )}
                </TreeView> 
            </>
            } 


            {/* Add and remove clusterlets alert */}
            {showAddNodes     && <AddClusteretsAlert transaction={transaction} transactionNodes={transactionClusterlets} selectedNodes={selectedNodes} setSelectedNodes={setSelectedNodes} onClose={onAddClusterletsClose} onCompletion={onAddclusterletsCompletion} /> } 
            {removeClusterlet && <RemoveClusterletAlert onClose={handleRemoveClusterletClose} nodeId={removeClusterlet} />}
            
            {/* Delete alerts */}
            {showEventDeleteAlert && itemToDelete && <DeleteEventAlert onDeleteConfirmation={onDeleteConfirmation} onCompletion={onEventDeleteComplete} setShowDeleteEventAlert={setShowEventDeleteAlert} eventId={itemToDelete?.id} onClose={onCloseEventDelete} />}
            {showDeleteTransactionAlert && itemToDelete  &&  <DeleteTransactionAlert onDeleteConfirmation={onDeleteConfirmation} onCompletion={onDeleteTransactionCompletion} element={itemToDelete} onClose={OnDeleteTransactionClose} /> } 
        </Paper>
        
    );
};

export default TransactionElements;
